import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { VehicleInspectionService } from 'src/app/services/inpection/vehicle-inspection.service';
import { InspectionManagerModalService } from 'src/app/services/inspection-manager-modal/inspection-manager-modal.service';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import { VendorService } from 'src/app/services/vendor/vendor.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-inspection-manager',
  templateUrl: './add-edit-inspection-manager.component.html',
  styleUrls: ['./add-edit-inspection-manager.component.scss']
})
export class AddEditInspectionManagerComponent extends DestructorComponent implements OnInit {

  @Output() inspectionAdded = new EventEmitter<void>();
  @Output() inspectionUpdated = new EventEmitter<void>();

  inspectionForm: FormGroup;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedInspection: any = null;
  private preSelectedVehicle: any = null;
  private isVehicleDisabled: boolean = false;

  // Vehicle search properties
  vehicleSearchTerm: string = '';
  vehicleSearchResults: any[] = [];
  selectedVehicle: any = null;
  showVehicleDropdown: boolean = false;
  isVehicleSearching: boolean = false;
  private vehicleSearchSubject = new Subject<string>();

  // Inspection Agency search properties
  inspAgencySearchTerm: string = '';
  inspAgencySearchResults: any[] = [];
  selectedInspAgency: any = null;
  showInspAgencyDropdown: boolean = false;
  isInspAgencySearching: boolean = false;
  private inspAgencySearchSubject = new Subject<string>();

  // Countries list
  countriesList: any[] = [];

  // Inspection status options
  inspectionStatusOptions = [
    { value: 'In Process', label: 'In Process' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Passed', label: 'Passed' },
    { value: 'Failed', label: 'Failed' }
  ];

  constructor(
    private fb: FormBuilder,
    private _vehicleInspectionService: VehicleInspectionService,
    private _inspectionManagerModalService: InspectionManagerModalService,
    private _vehicleService: VehicleService,
    private _vendorService: VendorService,
    private _globalStateService: GlobalStateService
  ) {
    super();
    this.initializeForm();
    this.setupVehicleSearchDebounce();
    this.setupInspAgencySearchDebounce();
  }

  ngOnInit(): void {
    this.setupModalListener();
    this.loadCountries();
  }

  private initializeForm(): void {
    this.inspectionForm = this.fb.group({
      vehicle_id: ['', [Validators.required]],
      vehicle_name: ['', [Validators.required]],
      destination_country: ['', [Validators.required]],
      insp_agency: ['', [Validators.required]],
      insp_agency_id: [''],
      insp_apply_date: ['', [Validators.required]],
      insp_passed_date: [''],
      insp_status: ['', [Validators.required]],
      admin_comment: [''],
      cost: ['', [Validators.pattern(/^\d+(\.\d{1,2})?$/)]]
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_add_edit_inspection_manager');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceInspection = this._inspectionManagerModalService.getSelectedInspection();
        const preSelectedVehicle = this._inspectionManagerModalService.getPreSelectedVehicle();

        if (serviceInspection) {
          this.isEditMode = true;
          this.selectedInspection = serviceInspection;
          this.isVehicleDisabled = true;
          this.populateForm(this.selectedInspection);
        } else if (preSelectedVehicle) {
          this.isEditMode = false;
          this.selectedInspection = null;
          this.preSelectedVehicle = preSelectedVehicle;
          this.isVehicleDisabled = true;
          this.resetForm();
          this.populateVehicleFromPreSelected(preSelectedVehicle);
        } else {
          this.isEditMode = false;
          this.selectedInspection = null;
          this.preSelectedVehicle = null;
          this.isVehicleDisabled = false;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._inspectionManagerModalService.clearSelectedInspection();
        this._inspectionManagerModalService.clearPreSelectedVehicle();
        this.resetForm();
      });
    }
  }

  private loadCountries(): void {
    this.subscriptions.push(
      this._globalStateService.GetCountries().subscribe(countries => {
        this.countriesList = countries || [];
      })
    );
  }

  private populateForm(inspection: any): void {
    if (inspection.vehicle) {
      this.selectedVehicle = inspection.vehicle;
      this.vehicleSearchTerm = this.getVehicleDisplayName(inspection.vehicle);
    }

    if (inspection.insp_agency) {
      this.selectedInspAgency = inspection.insp_agency;
      this.inspAgencySearchTerm = this.getInspAgencyDisplayName(inspection.insp_agency);
    }

    this.inspectionForm.patchValue({
      vehicle_id: inspection.vehicle_id || inspection.vehicle?._id || inspection.vehicle?.id || '',
      vehicle_name: this.vehicleSearchTerm,
      destination_country: inspection.destination_country || '',
      insp_agency: this.inspAgencySearchTerm,
      insp_agency_id: inspection.insp_agency_id || inspection.insp_agency_data?._id || '',
      insp_apply_date: inspection.insp_apply_date || '',
      insp_passed_date: inspection.insp_passed_date || '',
      insp_status: inspection.insp_status || '',
      admin_comment: inspection.admin_comment || '',
      cost: inspection.cost || ''
    });
  }

  private populateVehicleFromPreSelected(vehicle: any): void {
    this.selectedVehicle = vehicle;
    this.vehicleSearchTerm = this.getVehicleDisplayName(vehicle);
    this.inspectionForm.patchValue({
      vehicle_id: vehicle._id || vehicle.id,
      vehicle_name: this.vehicleSearchTerm
    });
  }

  private resetForm(): void {
    this.inspectionForm.reset();
    this.selectedVehicle = null;
    this.selectedInspAgency = null;
    this.vehicleSearchTerm = '';
    this.inspAgencySearchTerm = '';
    this.vehicleSearchResults = [];
    this.inspAgencySearchResults = [];
    this.showVehicleDropdown = false;
    this.showInspAgencyDropdown = false;
    this.isLoading = false;
  }

  closeModal(): void {
    const modalElement = document.getElementById('kt_modal_add_edit_inspection_manager');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  // Vehicle search methods
  private setupVehicleSearchDebounce(): void {
    this.vehicleSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchVehicles(searchTerm);
    });
  }

  onVehicleSearchInput(event: Event): void {
    if (this.isVehicleDisabled) {
      return;
    }

    const target = event.target as HTMLInputElement;
    this.vehicleSearchTerm = target.value;
    this.selectedVehicle = null;

    this.inspectionForm.patchValue({
      vehicle_name: this.vehicleSearchTerm,
      vehicle_id: ''
    });

    if (this.vehicleSearchTerm.length >= 2) {
      this.vehicleSearchSubject.next(this.vehicleSearchTerm);
      this.showVehicleDropdown = true;
    } else {
      this.showVehicleDropdown = false;
      this.vehicleSearchResults = [];
    }
  }

  onVehicleInputFocus(): void {
    if (this.isVehicleDisabled) {
      return;
    }

    if (this.vehicleSearchResults.length > 0) {
      this.showVehicleDropdown = true;
    }
  }

  onVehicleInputBlur(): void {
    setTimeout(() => {
      this.showVehicleDropdown = false;
    }, 200);
  }

  private searchVehicles(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.vehicleSearchResults = [];
      this.showVehicleDropdown = false;
      return;
    }

    this.isVehicleSearching = true;
    this.subscriptions.push(
      this._vehicleService.GetData('1', searchTerm, { paginate: false, search: searchTerm }).subscribe({
        next: (response: any) => {
          this.isVehicleSearching = false;
          if (response.status && response.data) {
            this.vehicleSearchResults = response.data.data || response.data || [];
            this.showVehicleDropdown = this.vehicleSearchResults.length > 0;
          } else {
            this.vehicleSearchResults = [];
            this.showVehicleDropdown = false;
          }
        },
        error: (error: any) => {
          this.isVehicleSearching = false;
          console.error('Error searching vehicles:', error);
          this.vehicleSearchResults = [];
          this.showVehicleDropdown = false;
        }
      })
    );
  }

  selectVehicle(vehicle: any): void {
    this.selectedVehicle = vehicle;
    this.vehicleSearchTerm = this.getVehicleDisplayName(vehicle);

    this.inspectionForm.patchValue({
      vehicle_name: this.vehicleSearchTerm,
      vehicle_id: vehicle._id || vehicle.id
    });

    this.showVehicleDropdown = false;
  }

  private getVehicleDisplayName(vehicle: any): string {
    const parts = [];
    if (vehicle.name) parts.push(vehicle.name);
    if (vehicle.company_name) parts.push(vehicle.company_name);
    if (vehicle.year) parts.push(`(${vehicle.year})`);
    return parts.join(' ') || 'Unknown Vehicle';
  }

  getVehicleImageUrl(vehicle: any): string {
    if (vehicle.pictures && vehicle.pictures.length > 0) {
      return vehicle.pictures[0];
    }
    return 'assets/media/svg/files/blank-image.svg';
  }

  // Inspection Agency search methods
  private setupInspAgencySearchDebounce(): void {
    this.inspAgencySearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchInspAgencies(searchTerm);
    });
  }

  onInspAgencySearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.inspAgencySearchTerm = target.value;
    this.selectedInspAgency = null;

    this.inspectionForm.patchValue({
      insp_agency: this.inspAgencySearchTerm,
      insp_agency_id: ''
    });

    if (this.inspAgencySearchTerm.length >= 2) {
      this.inspAgencySearchSubject.next(this.inspAgencySearchTerm);
      this.showInspAgencyDropdown = true;
    } else {
      this.showInspAgencyDropdown = false;
      this.inspAgencySearchResults = [];
    }
  }

  onInspAgencyInputFocus(): void {
    if (this.inspAgencySearchResults.length > 0) {
      this.showInspAgencyDropdown = true;
    }
  }

  onInspAgencyInputBlur(): void {
    setTimeout(() => {
      this.showInspAgencyDropdown = false;
    }, 200);
  }

  private searchInspAgencies(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.inspAgencySearchResults = [];
      this.showInspAgencyDropdown = false;
      return;
    }

    this.isInspAgencySearching = true;
    this.subscriptions.push(
      this._vendorService.GetData('1', searchTerm, { paginate: false, type: 'inspection_agency' }).subscribe({
        next: (response: any) => {
          this.isInspAgencySearching = false;
          if (response.status && response.data) {
            this.inspAgencySearchResults = response.data.data || response.data || [];
            this.showInspAgencyDropdown = this.inspAgencySearchResults.length > 0;
          } else {
            this.inspAgencySearchResults = [];
            this.showInspAgencyDropdown = false;
          }
        },
        error: (error: any) => {
          this.isInspAgencySearching = false;
          console.error('Error searching inspection agencies:', error);
          this.inspAgencySearchResults = [];
          this.showInspAgencyDropdown = false;
        }
      })
    );
  }

  selectInspAgency(agency: any): void {
    this.selectedInspAgency = agency;
    this.inspAgencySearchTerm = this.getInspAgencyDisplayName(agency);

    this.inspectionForm.patchValue({
      insp_agency: this.inspAgencySearchTerm,
      insp_agency_id: agency._id || agency.id
    });

    this.showInspAgencyDropdown = false;
  }

  private getInspAgencyDisplayName(agency: any): string {
    return agency.name || agency.company_name || 'Unknown Agency';
  }

  // Form submission
  onSubmit(): void {
    if (this.inspectionForm.valid) {
      this.isLoading = true;
      const formData = { ...this.inspectionForm.value };

      // Remove display fields
      delete formData.vehicle_name;

      if (this.isEditMode && this.selectedInspection) {
        this.updateInspection(formData);
      } else {
        this.addInspection(formData);
      }
    } else {
      this.markFormGroupTouched(this.inspectionForm);
      Swal.fire({
        title: 'Validation Error',
        text: 'Please fill in all required fields correctly.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  }

  private addInspection(data: any): void {
    this.subscriptions.push(
      this._vehicleInspectionService.AddData(data).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Inspection record added successfully',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.inspectionAdded.emit();
            });
          } else {
            this.handleError(response.message || 'Failed to add inspection record');
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          this.handleError(error.error?.message || 'An error occurred while adding inspection record');
        }
      })
    );
  }

  private updateInspection(data: any): void {
    const inspectionId = this.selectedInspection._id || this.selectedInspection.id;
    this.subscriptions.push(
      this._vehicleInspectionService.UpdateData(inspectionId, data).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Inspection record updated successfully',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.inspectionUpdated.emit();
            });
          } else {
            this.handleError(response.message || 'Failed to update inspection record');
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          this.handleError(error.error?.message || 'An error occurred while updating inspection record');
        }
      })
    );
  }

  private handleError(message: string): void {
    Swal.fire({
      title: 'Error!',
      text: message,
      icon: 'error',
      confirmButtonText: 'OK'
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Form control getters
  get vehicle_id() { return this.inspectionForm.get('vehicle_id'); }
  get vehicle_name() { return this.inspectionForm.get('vehicle_name'); }
  get destination_country() { return this.inspectionForm.get('destination_country'); }
  get insp_agency() { return this.inspectionForm.get('insp_agency'); }
  get insp_apply_date() { return this.inspectionForm.get('insp_apply_date'); }
  get insp_passed_date() { return this.inspectionForm.get('insp_passed_date'); }
  get insp_status() { return this.inspectionForm.get('insp_status'); }
  get admin_comment() { return this.inspectionForm.get('admin_comment'); }
  get cost() { return this.inspectionForm.get('cost'); }

  // Utility getters
  get isVehicleSelectionDisabled(): boolean {
    return this.isVehicleDisabled;
  }
}
